/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Import API client
import captureClient, { isSessionError, getErrorMessage } from '../api/captureClient';

// Import extracted components
import { Capturing, Done, Instructions, Review, Submitting, Welcome } from '../components/capture';
import Modal from '../components/Modal';

// Import extracted utilities
import { requestLocation } from '../utils/captureUtils';
import { sendProvenanceEvent } from '../utils/provenanceService';

const HTTP_STATUS_NOT_FOUND = 404;
const HTTP_STATUS_GONE = 410;
const HTTP_STATUS_INTERNAL_SERVER_ERROR = 500;

const Capture = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [captureData, setCaptureData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Phases: welcome → instructions → capturing → review → submitting → done
  const [phase, setPhase] = useState('welcome');
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  // Items already completed on the server (from a previous session)
  const [serverCompleted, setServerCompleted] = useState({});
  // Session recovery state
  const [sessionRecovered, setSessionRecovered] = useState(false);
  const [completedItemsMedia, setCompletedItemsMedia] = useState({});
  // Files captured in this session, held locally before final submit
  // { [assignmentItemId]: { file, previewUrl, identifierText, item } }
  const [capturedFiles, setCapturedFiles] = useState({});
  // Upload progress per item during submit phase
  const submitProgress = { current: 0, total: 0, itemIndex: 0 };
  const [submitError, setSubmitError] = useState('');
  const [location, setLocation] = useState(null);
  const startedRef = useRef(false);
  const fetchInProgressRef = useRef(false);
  // Track which item we came from when retaking from review
  const retakeItemIdRef = useRef(null);
  // Mirrors capturedFiles so the unmount cleanup below can read the latest
  // value instead of closing over the value from the effect's first render.
  const capturedFilesRef = useRef(capturedFiles);
  // Session conflict state
  const [showSessionConflictModal, setShowSessionConflictModal] = useState(false);
  const [sessionConflictBranding, setSessionConflictBranding] = useState(null);

  const fetchCaptureData = useCallback(async (skipNavigation = false, forceNew = false) => {
    // Guard: if a fetch is already in-flight and this isn't a forced new-session call,
    // drop the duplicate (caused by React Strict Mode double-invoking useEffect).
    if (fetchInProgressRef.current && !forceNew) return;
    fetchInProgressRef.current = true;
    setLoading(true);

    try {
      // If forceNew, request a new session first
      if (forceNew) {
        await captureClient.post(`/${token}/force-new-session`);
        // Session token will be stored by the interceptor
      }

      // Call capture API - session token will be auto-added by interceptor
      const response = await captureClient.get(`/${token}`);

      setCaptureData(response.data);

      // Check if assignment is already completed
      if (response.data.assignment.status === 'completed') {
        // Still need to populate serverCompleted for the Done component
        const initialItems = response.data.items;
        const initialCompletedItems = {};
        const initialCompletedMedia = {};
        let completedCount = 0;

        initialItems.forEach(item => {
          if (item.status === 'completed') {
            initialCompletedItems[item.assignmentItemId] = true;
            completedCount++;

            // Store completed media for display
            if (item.media) {
              initialCompletedMedia[item.assignmentItemId] = {
                url: item.media.url,
                fileName: item.media.fileName,
                mimeType: item.media.mimeType,
                fileSize: item.media.fileSize,
                type: item.type,
                completedAt: item.completedAt,
                duration: item.media.duration,
                identifierText: item.customerIdentifierText ?? item.identifierText,
                customerIdentifierText: item.customerIdentifierText ?? item.identifierText,
                ocrIdentifierText: item.identifierText,
              };
            }
          }
        });

        setServerCompleted(initialCompletedItems);
        setCompletedItemsMedia(initialCompletedMedia);

        // Set the completed count in captureData for Done component
        setCaptureData(prev => ({
          ...prev,
          completedItemsCount: completedCount
        }));

        if (!skipNavigation) {
          setPhase('done');
        }
        return;
      }

      // Session Recovery Logic
      const fetchedItems = response.data.items;
      const completedItems = {};
      const completedMedia = {};

      fetchedItems.forEach(item => {
        if (item.status === 'completed') {
          completedItems[item.assignmentItemId] = true;

          // Store completed media for display
          if (item.media) {
            completedMedia[item.assignmentItemId] = {
              url: item.media.url,
              fileName: item.media.fileName,
              mimeType: item.media.mimeType,
              fileSize: item.media.fileSize,
              type: item.type,
              completedAt: item.completedAt,
              duration: item.media.duration,
              identifierText: item.customerIdentifierText ?? item.identifierText,
              customerIdentifierText: item.customerIdentifierText ?? item.identifierText,
              ocrIdentifierText: item.identifierText,
            };
          }
        }
      });

      setServerCompleted(completedItems);
      setCompletedItemsMedia(completedMedia);

      // Only handle navigation if skipNavigation is false
      if (!skipNavigation) {
        // Determine starting phase and step
        const hasCompletedItems = Object.keys(completedItems).length > 0;

        // Find first pending REQUIRED step (skip optional steps)
        const firstPendingRequiredIndex = fetchedItems.findIndex(item =>
          item.required && item.status === 'pending'
        );

        // Fallback: find any pending step if no required steps are pending
        const firstPendingIndex = fetchedItems.findIndex(item => item.status === 'pending');

        const lastCompletedIndex = fetchedItems.map((item, index) =>
          (item.status === 'completed' || item.status === 'skipped') ? index : -1
        ).filter(index => index !== -1).pop(); // Get the last completed/skipped item index

        if (response.data.assignment.status === 'in_progress' && hasCompletedItems) {
          // Session recovery scenario
          setSessionRecovered(true);

          // Check if all required steps are completed
          const allRequiredCompleted = fetchedItems
            .filter(item => item.required)
            .every(item => item.status === 'completed');

          if (allRequiredCompleted) {
            // All required steps done, go to review
            setPhase('review');
          } else if (firstPendingRequiredIndex >= 0 && lastCompletedIndex !== undefined) {
            // Resume from last completed item (n-1) instead of next pending required (n)
            setCurrentStepIndex(lastCompletedIndex);
            setPhase('capturing');
          } else if (firstPendingRequiredIndex >= 0) {
            // Fallback: Resume from first pending required item
            setCurrentStepIndex(firstPendingRequiredIndex);
            setPhase('capturing');
          } else if (firstPendingIndex >= 0) {
            // Only optional steps remaining
            setCurrentStepIndex(firstPendingIndex);
            setPhase('capturing');
          } else {
            // All items completed, go to review
            setPhase('review');
          }
        } else if (response.data.assignment.status === 'in_progress') {
          setPhase('capturing');
          setCurrentStepIndex(firstPendingRequiredIndex >= 0 ? firstPendingRequiredIndex : (firstPendingIndex >= 0 ? firstPendingIndex : 0));
        } else {
          setPhase('welcome');
        }
      }

    } catch (err) {
      // Handle session conflict specifically
      if (err.response?.status === 409 && err.response?.data?.sessionConflict) {
        setSessionConflictBranding(err.response.data.branding || null);
        setShowSessionConflictModal(true);
        setLoading(false);
        return;
      }

      // Handle session-specific errors
      if (isSessionError(err)) {
        const userMessage = getErrorMessage(err);
        navigate('/error', {
          replace: true,
          state: {
            type: 'session_error',
            message: userMessage,
            branding: null,
            from: window.location.pathname + window.location.search
          },
        });
        return;
      }

      const data = err.response?.data;

      // Map HTTP status + error type to our error type keys
      let type = 'default';
      if (!navigator.onLine) {
        type = 'offline';
      } else if (err.response?.status === HTTP_STATUS_NOT_FOUND) {
        // 404: truly not found
        type = data?.type ?? 'not_found';
      } else if (err.response?.status === HTTP_STATUS_GONE) {
        // 410: gone — could be 'deleted', 'expired', 'completed', etc.
        type = data?.type ?? 'expired';
      } else if (data?.type) {
        type = data.type;
      } else {
        type = 'default';
      }

      navigate('/error', {
        replace: true,
        state: { type, branding: null, from: window.location.pathname + window.location.search },
      });
    } finally {
      setLoading(false);
      fetchInProgressRef.current = false;
    }
  }, [token, navigate]);

  // New function to fetch updated media after upload
  const fetchUpdatedMedia = useCallback(async (assignmentItemId) => {
    try {
      const response = await captureClient.get(`/${token}/media/${assignmentItemId}`);

      if (response.data.media) {
        // Update completed items media with fresh TruVideo URL
        setCompletedItemsMedia(prev => ({
          ...prev,
          [assignmentItemId]: {
            url: response.data.media.url,
            fileName: response.data.media.fileName,
            mimeType: response.data.media.mimeType,
            fileSize: response.data.media.fileSize,
            type: response.data.media.type,
            completedAt: response.data.media.completedAt,
            identifierText: response.data.media.customerIdentifierText ?? response.data.media.identifierText,
            customerIdentifierText: response.data.media.customerIdentifierText ?? response.data.media.identifierText,
            ocrIdentifierText: response.data.media.identifierText,
            duration: parseInt(response.data.media.duration)
          }
        }));

        // Update captured files to replace blob URL with TruVideo URL
        setCapturedFiles(prev => {
          const updated = { ...prev };
          if (updated[assignmentItemId]) {
            // Revoke old blob URL to free memory
            if (updated[assignmentItemId].previewUrl) {
              URL.revokeObjectURL(updated[assignmentItemId].previewUrl);
            }

            // Replace with TruVideo URL
            updated[assignmentItemId] = {
              ...updated[assignmentItemId],
              previewUrl: response.data.media.url,
              truVideoUrl: response.data.media.url,
              uploaded: true
            };
          }
          return updated;
        });
      }
    } catch (error) {
      console.error('Failed to fetch updated media:', error);
    }
  }, [token]);

  // Apply sub-account favicon when capture data is loaded
  useEffect(() => {
    const faviconUrl = captureData?.tenant?.faviconUrl;
    if (!faviconUrl) { return; }

    const existing = document.querySelector("link[rel~='icon']");
    const link = existing || document.createElement('link');
    link.rel = 'icon';
    link.href = faviconUrl;
    if (!existing) {
      document.head.appendChild(link);
    }

    // Restore default favicon on unmount
    return () => {
      link.href = '/favicon.ico';
    };
  }, [captureData?.tenant?.faviconUrl]);

  useEffect(() => {
    fetchCaptureData();
  }, [fetchCaptureData]);

  // Handler for canceling session conflict modal
  const handleCancelSessionConflict = () => {
    setShowSessionConflictModal(false);
    navigate('/error', {
      replace: true,
      state: {
        type: 'session_error',
        message: 'This link has been opened on another device',
        branding: sessionConflictBranding,
        from: window.location.pathname + window.location.search
      }
    });
  };

  // Call start API when landing on welcome screen
  useEffect(() => {
    const markAsStarted = async () => {
      if (phase === 'welcome' && !startedRef.current && !loading) {
        startedRef.current = true;
        try {
          // Request location first
          const loc = await requestLocation();
          setLocation(loc);

          await captureClient.post(`/${token}/start`);

          // Capture provenance: assignment_opened (only once per browser per assignment)
          const provenanceKey = `${token}_provenance_captured`;
          if (captureData?.workflow?.captureProvenanceDetails && !localStorage.getItem(provenanceKey)) {
            sendProvenanceEvent(token, 'assignment_opened', loc);
            localStorage.setItem(provenanceKey, 'true');
          }
        } catch (err) {
          console.error('Failed to mark as opened:', err);
        }
      }
    };

    markAsStarted();
  }, [phase, loading, token, captureData]);

  // Keep the ref in sync so the unmount cleanup below always sees the latest captured files.
  useEffect(() => {
    capturedFilesRef.current = capturedFiles;
  }, [capturedFiles]);

  // Clean up blob URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(capturedFilesRef.current).forEach(f => {
        if (f.previewUrl) { URL.revokeObjectURL(f.previewUrl); }
      });
    };
  }, []);

  // Navigate to error page if connection is lost
  useEffect(() => {
    const handleOffline = () => {
      navigate('/error', {
        replace: true,
        state: {
          type: 'offline',
          branding: captureData?.workflow?.branding,
          from: window.location.pathname + window.location.search
        },
      });
    };
    window.addEventListener('offline', handleOffline);
    return () => window.removeEventListener('offline', handleOffline);
  }, [navigate, captureData]);

  // Show conflict modal when a mid-session 409 fires during any POST/DELETE call
  // (e.g. upload, start). The captureClient interceptor dispatches this event
  // so the user is clearly informed their session is active on another device.
  useEffect(() => {
    const handleMidSessionConflict = () => {
      setShowSessionConflictModal(true);
    };
    window.addEventListener('capture-session-conflict', handleMidSessionConflict);
    return () => window.removeEventListener('capture-session-conflict', handleMidSessionConflict);
  }, []);

  const handleViewInstructions = () => {
    setPhase('instructions');
  };

  const handleStart = async () => {
    try {
      // Call the new API to mark assignment as in_progress
      await captureClient.post(`/${token}/begin-capture`);

    } catch (err) {
      console.error('Failed to mark assignment as in_progress:', err);
      if (err.response?.status === HTTP_STATUS_INTERNAL_SERVER_ERROR) {
        navigate('/error', {
          replace: true,
          state: { type: 'default', branding: captureData?.workflow?.branding, from: window.location.pathname + window.location.search },
        });
        return;
      }
      // Continue with capture flow even if status update fails
    }

    setCurrentStepIndex(0);
    setPhase('capturing');
  };

  // Called when user captures media for the current step
  const handleCapture = async (file, identifierText, uploadResult) => {
    const allStepItems = captureData.items;
    const currentItem = allStepItems[currentStepIndex];
    const previewUrl = URL.createObjectURL(file);

    // Revoke old preview URL if retaking
    if (capturedFiles[currentItem.assignmentItemId]?.previewUrl) {
      URL.revokeObjectURL(capturedFiles[currentItem.assignmentItemId].previewUrl);
    }

    const newCaptured = {
      ...capturedFiles,
      [currentItem.assignmentItemId]: {
        file,
        previewUrl,
        identifierText,
        customerIdentifierText: uploadResult?.customerIdentifierText ?? identifierText,
        ocrIdentifierText: uploadResult?.identifierText ?? null,
        item: currentItem,
        uploaded: !!uploadResult,
        uploadResult: uploadResult || null
      }
    };
    setCapturedFiles(newCaptured);

    // If uploaded successfully, mark as server completed and fetch updated media
    if (uploadResult) {
      setServerCompleted(prev => ({
        ...prev,
        [currentItem.assignmentItemId]: true
      }));

      // Update the item status in captureData to 'completed'
      setCaptureData(prev => ({
        ...prev,
        items: prev.items.map(item =>
          item.assignmentItemId === currentItem.assignmentItemId
            ? { ...item, status: 'completed' }
            : item
        )
      }));

      // Fetch updated media URL from TruVideo to replace blob URL
      fetchUpdatedMedia(currentItem.assignmentItemId);
    }

    // Clear retake mode when capturing new item (not from retake)
    if (retakeItemIdRef.current !== currentItem.assignmentItemId) {
      retakeItemIdRef.current = null;
    }

    // If we came here from "Retake" in review, go back to review
    if (retakeItemIdRef.current === currentItem.assignmentItemId) {
      retakeItemIdRef.current = null;
      // Refresh data from server before going to review
      await fetchCaptureData(true);
      setPhase('review');
      return;
    }

    // For sequential navigation after retake, go to next step if available
    if (currentStepIndex < allStepItems.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      // We're at the last step, refresh data and go to review
      await fetchCaptureData(true);
      setPhase('review');
    }
  };

  const handleSkip = async () => {
    const allStepItems = captureData.items;
    const currentItem = allStepItems[currentStepIndex];

    if (!currentItem.required) {
      try {
        const formData = new FormData();
        formData.append('assignmentItemId', currentItem.assignmentItemId);
        formData.append('skip', 'true');

        // Call standard upload endpoint with skip=true
        const response = await captureClient.post(
          `/${token}/upload`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        const newStatus = response.data.alreadyCaptured ? 'completed' : 'skipped';

        // Update the item status locally to skipped or completed
        setCaptureData(prev => ({
          ...prev,
          items: prev.items.map(item =>
            item.assignmentItemId === currentItem.assignmentItemId
              ? { ...item, status: newStatus }
              : item
          )
        }));
      } catch (err) {
        console.error('Failed to skip item on server:', err);
      }
    }

    // If we're in retake mode and user skips, clear the retake flag
    if (retakeItemIdRef.current === currentItem.assignmentItemId) {
      retakeItemIdRef.current = null;
      // Refresh data from server before going to review
      await fetchCaptureData(true);
      setPhase('review');
      return;
    }

    if (currentStepIndex < allStepItems.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      // Refresh data from server before going to review
      await fetchCaptureData(true);
      setPhase('review');
    }
  };

  const handleDelete = async (assignmentItemId) => {
    try {
      await captureClient.delete(`/${token}/item/${assignmentItemId}`);

      // Clear from capturedFiles
      if (capturedFiles[assignmentItemId]) {
        if (capturedFiles[assignmentItemId].previewUrl) {
          URL.revokeObjectURL(capturedFiles[assignmentItemId].previewUrl);
        }
        setCapturedFiles(prev => {
          const updated = { ...prev };
          delete updated[assignmentItemId];
          return updated;
        });
      }

      // Clear from serverCompleted
      setServerCompleted(prev => {
        const updated = { ...prev };
        delete updated[assignmentItemId];
        return updated;
      });

      // Clear from completedItemsMedia
      setCompletedItemsMedia(prev => {
        const updated = { ...prev };
        delete updated[assignmentItemId];
        return updated;
      });

      // Update item status in captureData
      setCaptureData(prev => ({
        ...prev,
        items: prev.items.map(item =>
          item.assignmentItemId === assignmentItemId
            ? { ...item, status: 'pending' }
            : item
        )
      }));
    } catch (error) {
      console.error('Failed to delete item:', error);
      if (error.response?.status === HTTP_STATUS_INTERNAL_SERVER_ERROR) {
        navigate('/error', {
          replace: true,
          state: { type: 'default', branding: captureData?.workflow?.branding, from: window.location.pathname + window.location.search },
        });
        return;
      }
      setSubmitError('Failed to delete item. Please try again.');
    }
  };

  // From review screen: retake a specific item
  const handleRetake = (assignmentItemId, fromReview = true) => {
    const allStepItems = captureData.items;
    const idx = allStepItems.findIndex(i => i.assignmentItemId === assignmentItemId);

    // Clear server completed status if retaking an already uploaded item
    if (serverCompleted[assignmentItemId]) {
      setServerCompleted(prev => {
        const updated = { ...prev };
        delete updated[assignmentItemId];
        return updated;
      });
    }

    // Clear completed media if exists
    if (completedItemsMedia[assignmentItemId]) {
      setCompletedItemsMedia(prev => {
        const updated = { ...prev };
        delete updated[assignmentItemId];
        return updated;
      });
    }

    // ALSO clear captured files for this item
    if (capturedFiles[assignmentItemId]) {
      setCapturedFiles(prev => {
        const updated = { ...prev };
        delete updated[assignmentItemId];
        return updated;
      });
    }

    // Only set retakeItemIdRef if coming from review screen
    if (fromReview) {
      retakeItemIdRef.current = assignmentItemId;
    }

    setCurrentStepIndex(idx);
    setPhase('capturing');
  };

  const handleSubmit = async () => {
    const requiredItems = captureData.items.filter(item => item.required);
    const uploadedItems = Object.values(capturedFiles).filter(cf => cf.uploaded);
    const serverCompletedCount = Object.keys(serverCompleted).length;

    if (requiredItems.length > uploadedItems.length + serverCompletedCount) {
      setSubmitError('Please complete all required items before submitting.');
      return;
    }

    setSubmitError('');

    try {
      // Just mark assignment as completed - files already uploaded
      const response = await captureClient.post(`/${token}/complete`);

      // Store the completed items count from API response
      const completedItemsCount = response.data.completedItemsCount;

      // Update captureData to include the completed count for Done component
      setCaptureData(prev => ({
        ...prev,
        completedItemsCount
      }));

      localStorage.removeItem(`${token}_provenance_captured`);
      setPhase('done');
    } catch (err) {
      console.error('Failed to complete assignment:', err);
      if (err.response?.status === HTTP_STATUS_INTERNAL_SERVER_ERROR) {
        navigate('/error', {
          replace: true,
          state: { type: 'default', branding: captureData?.workflow?.branding, from: window.location.pathname + window.location.search },
        });
        return;
      }
      setSubmitError(err.response?.data?.message || 'Failed to complete assignment. Please try again.');
      setPhase('review');
    }
  };

  const handlePreviousStep = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    } else {
      setPhase('instructions');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-base-content text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // If there's nothing to show at all, bail out
  if (!captureData && !showSessionConflictModal) { return null; }

  // Extract common variables (safe: only reached when captureData is non-null
  // OR when the conflict modal is open and captureData is still null)
  const assignment = captureData?.assignment;
  const workflow = captureData?.workflow;
  const tenant = captureData?.tenant;
  const items = captureData?.items;
  const branding = workflow?.branding || {};
  const pageBgStyle = { background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)' };
  const brandSolidStyle = branding.primaryColor ? { backgroundColor: branding.primaryColor } : null;
  const welcomeBgClass = 'bg-base-200';
  const accentBgClass = 'bg-primary';

  // Phase routing
  const renderPhase = () => {
    switch (phase) {
      case 'welcome':
        return (
          <Welcome
            assignment={assignment}
            workflow={workflow}
            tenant={tenant}
            branding={branding}
            pageBgStyle={pageBgStyle}
            welcomeBgClass={welcomeBgClass}
            onViewInstructions={handleViewInstructions}
          />
        );
      case 'instructions':
        return (
          <Instructions
            assignment={assignment}
            items={items}
            branding={branding}
            tenant={tenant}
            workflow={workflow}
            serverCompleted={serverCompleted}
            pageBgStyle={pageBgStyle}
            welcomeBgClass={welcomeBgClass}
            onStart={handleStart}
            onBack={() => setPhase('welcome')}
          />
        );
      case 'capturing':
        return (
          <Capturing
            assignment={assignment}
            workflow={workflow}
            tenant={tenant}
            currentItem={items[currentStepIndex]}
            currentStepIndex={currentStepIndex}
            items={items}
            capturedFiles={capturedFiles}
            serverCompleted={serverCompleted}
            completedItemsMedia={completedItemsMedia}
            sessionRecovered={sessionRecovered}
            branding={branding}
            pageBgStyle={pageBgStyle}
            welcomeBgClass={welcomeBgClass}
            accentBgClass={accentBgClass}
            brandSolidStyle={brandSolidStyle}
            onCapture={handleCapture}
            onSkip={handleSkip}
            onReview={() => setPhase('review')}
            onPrevious={handlePreviousStep}
            onRetake={handleRetake}
            token={token}
            retakeItemId={retakeItemIdRef.current}
            location={location}
          />
        );
      case 'review':
        return (
          <Review
            items={items}
            capturedFiles={capturedFiles}
            serverCompleted={serverCompleted}
            completedItemsMedia={completedItemsMedia}
            branding={branding}
            workflow={workflow}
            submitError={submitError}
            pageBgStyle={pageBgStyle}
            welcomeBgClass={welcomeBgClass}
            assignment={assignment}
            onRetake={handleRetake}
            onSubmit={handleSubmit}
            onStart={handleStart}
            onDelete={handleDelete}
          />
        );
      case 'submitting':
        return (
          <Submitting
            submitProgress={submitProgress}
            branding={branding}
            pageBgStyle={pageBgStyle}
            welcomeBgClass={welcomeBgClass}
          />
        );
      case 'done':
        return (
          <Done
            assignment={assignment}
            tenant={tenant}
            branding={branding}
            workflow={workflow}
            items={items}
            completedItemsCount={captureData.completedItemsCount}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {/* Only render the capture phases once captureData is available */}
      {captureData && renderPhase()}

      <Modal
        isOpen={showSessionConflictModal}
        onClose={handleCancelSessionConflict}
        title="Active Session Detected"
      >
        {/* Icon */}
        <div className="w-[52px] h-[52px] rounded-full bg-primary/15 flex items-center justify-center mx-auto mb-4 text-primary">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="26"
            height="26"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>

        {/* Body text */}
        <p className="text-sm text-base-content/70 text-center leading-relaxed mb-6">
          You already have an active session open on another device or browser.
          <strong className="text-base-content"> Please continue on your previous device.</strong>
        </p>

        {/* Actions */}
        {/* <div className="flex flex-col gap-3">
          <button
            id="session-conflict-start-new"
            onClick={handleStartNewSession}
            className="w-full py-3 px-4 rounded-xl text-sm font-semibold text-primary-content bg-primary transition-opacity hover:opacity-90 active:scale-[0.98]"
          >
            Yes, Start New Session
          </button>

          <button
            id="session-conflict-cancel"
            onClick={handleCancelSessionConflict}
            className="w-full py-3 px-4 rounded-xl border border-base-content/20 text-sm font-medium text-base-content hover:bg-base-200 transition-colors"
          >
            No, Continue on Previous Device
          </button>
        </div> */}
      </Modal>
    </>
  );
};

export default Capture;
